import { getPinnedSidebarPreferenceKey, PinnedSidebarContext, type PinnedItem } from '@/contexts/pinned-sidebar.context'
import { useActiveOrg } from '@/hooks/use-active-org'
import { useUserPreference } from '@/hooks/use-user-preference'
import { useCallback, useMemo, type ReactNode } from 'react'

interface PinnedSidebarProviderProps {
  children: ReactNode
}

/**
 * Provider for Pinned Sidebar Items
 * Saves pinned items to backend via user preferences
 * Pinned items are organization-specific
 */
export const PinnedSidebarProvider = ({ children }: PinnedSidebarProviderProps) => {
  const { userPreferences, upsertUserPreference, isLoading } = useUserPreference()
  const { activeOrg } = useActiveOrg()

  // Generate organization-specific preference key
  const preferenceKey = useMemo(() => getPinnedSidebarPreferenceKey(activeOrg?.id), [activeOrg?.id])

  // Parse pinned items from user preferences for the current organization
  const pinnedItems: PinnedItem[] = useMemo(() => {
    const preference = userPreferences?.find((pref) => pref.key === preferenceKey)
    if (!preference?.value) return []

    try {
      const parsed = JSON.parse(preference.value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }, [userPreferences, preferenceKey])

  // Check if an item is pinned
  const isPinned = useCallback(
    (url: string) => {
      return pinnedItems.some((item) => item.url === url)
    },
    [pinnedItems]
  )

  // Save pinned items to backend for the current organization
  const savePinnedItems = useCallback(
    async (items: PinnedItem[]) => {
      // Only save url and title (icon can't be serialized)
      const serializable = items.map(({ title, url }) => ({ title, url }))
      await upsertUserPreference(preferenceKey, JSON.stringify(serializable), true)
    },
    [upsertUserPreference, preferenceKey]
  )

  // Pin an item
  const pinItem = useCallback(
    async (item: PinnedItem) => {
      if (isPinned(item.url)) return
      const newItems = [...pinnedItems, { title: item.title, url: item.url }]
      await savePinnedItems(newItems)
    },
    [pinnedItems, isPinned, savePinnedItems]
  )

  // Unpin an item
  const unpinItem = useCallback(
    async (url: string) => {
      const newItems = pinnedItems.filter((item) => item.url !== url)
      await savePinnedItems(newItems)
    },
    [pinnedItems, savePinnedItems]
  )

  // Toggle pin status
  const togglePin = useCallback(
    async (item: PinnedItem) => {
      if (isPinned(item.url)) await unpinItem(item.url)
      else await pinItem(item)
    },
    [isPinned, pinItem, unpinItem]
  )

  return (
    <PinnedSidebarContext.Provider
      value={{
        pinnedItems,
        isPinned,
        pinItem,
        unpinItem,
        togglePin,
        isLoading,
      }}
    >
      {children}
    </PinnedSidebarContext.Provider>
  )
}
